/* eslint-disable */
import PropTypes from 'prop-types';
import React from 'react';
import { DialogTitle, DialogContent, DialogActions, Typography, IconButton, Box, Grid } from '@mui/material';
import {
    Close as CloseIcon,
    Person as PersonIcon,
    Business as BusinessIcon,
    AccountBalance as AccountBalanceIcon,
    Email as EmailIcon,
    Badge as BadgeIcon,
    Article as ArticleIcon,
    ReceiptLong as ReceiptLongIcon,
    AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material';
import { theme, ActionButton, PreviewDialog } from '../../../ConstantColors';
import { useKeycloak } from '@react-keycloak/web';
import { useAcquittalData } from 'hooks/api/travel-acquital/useAcquittalData';
import { LabelValue } from 'ui-component/commons/LabelValue';
import { InfoSection } from 'ui-component/commons/InfoSection';
import { DocumentSection } from '../../../documents/DocumentSection';
import { formatCurrency, formatDate } from 'utils/formatters';
import AcquittalLineActivitiesFinanceTable from './AcquittalLineActivitiesFinanceTable';

const FinanceAcquittalPreviewDialog = ({ open, onClose, data }) => {
    const { keycloak } = useKeycloak();

    // Add default values for potentially missing data
    const acquittalData = {
        totalSpentAmount: 0,
        clientReceivedAmount: 0,
        varianceAmount: 0,
        breakfastVariance: 0,
        lunchVariance: 0,
        dinnerVariance: 0,
        incidentalVariance: 0,
        accommodationVariance: 0,
        provedAccommodationVariance: 0,
        unprovedAccommodationVariance: 0,
        branchCode: {},
        department: {},
        user: {},
        ...data
    };

    const { documents: acquittalDocuments, lineActivities } = useAcquittalData(acquittalData?.id, keycloak?.token);
    // Transform line activities to match the expected format
    const formattedLineActivities = lineActivities?.map(activity => ({
        id: activity.id,
        activityDate: activity.activityDate,
        breakfast: activity.breakfast || 0,
        lunch: activity.lunch || 0,
        dinner: activity.dinner || 0,
        incidental: activity.incidental || 0,
        accommodation: activity.accommodation || 0,
        miscellaneous: activity.miscellaneous || 0,
        // Add any additional fields needed by the table component
        total: (activity.breakfast || 0) + 
               (activity.lunch || 0) + 
               (activity.dinner || 0) + 
               (activity.incidental || 0) + 
               (activity.accommodation || 0) + 
               (activity.miscellaneous || 0)
    })) || [];

    return (
        <PreviewDialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
            <DialogTitle
                sx={{
                    bgcolor: theme.primary.main,
                    color: 'white',
                    py: 3,
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center'
                }}
            >
                <Box>
                    <Typography variant="h5" sx={{ fontWeight: 600 }}>
                        Acquittal Details - A{acquittalData.id}
                    </Typography>
                    <Typography variant="subtitle1" sx={{ mt: 1, opacity: 0.9 }}>
                        Created: {formatDate(acquittalData.createdAt)}
                    </Typography>
                </Box>
                <IconButton onClick={onClose} sx={{ color: 'white' }}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>

            <DialogContent sx={{ p: 4 }}>
                {/* Basic Information */}
                <InfoSection title="Basic Information" icon={ArticleIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Branch Code" value={acquittalData.branchCode?.code} chip />
                            <LabelValue label="Branch Name" value={acquittalData.branchCode?.name} icon={BusinessIcon} />
                            <LabelValue label="Branch Description" value={acquittalData.branchCode?.description} icon={BusinessIcon} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <LabelValue label="Department" value={acquittalData.department?.projectName} chip />
                            <LabelValue label="Group Code" value={acquittalData.department?.groupCode} icon={BusinessIcon} />
                            <LabelValue label="Group" value={acquittalData.department?.groupCodeDescription} icon={BusinessIcon} />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* User Information */}
                <InfoSection title="User Information" icon={PersonIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={6}>
                            <LabelValue
                                label="Full Name"
                                value={`${acquittalData.user?.firstName || ''} ${acquittalData.user?.lastName || ''}`}
                                icon={PersonIcon}
                            />
                            <LabelValue label="Email" value={acquittalData.user?.email} icon={EmailIcon} />
                            <LabelValue label="Employee Number" value={acquittalData.user?.employeeNumber} icon={BadgeIcon} />
                            <LabelValue label="Employee Title" value={acquittalData.user?.employeeTitle} icon={BadgeIcon} />
                            <LabelValue label="Status" value={acquittalData.user?.status} chip />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Financial Details */}
                <InfoSection title="Financial Details" icon={AccountBalanceIcon}>
                    <Grid container spacing={4}>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Total Spent Amount"
                                value={formatCurrency(acquittalData.totalSpentAmount)}
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Received Amount"
                                value={formatCurrency(acquittalData.clientReceivedAmount)}
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                        <Grid item xs={12} md={4}>
                            <LabelValue
                                label="Variance Amount"
                                value={formatCurrency(acquittalData.varianceAmount)}
                                chip
                                icon={AttachMoneyIcon}
                            />
                        </Grid>
                    </Grid>
                </InfoSection>

                {/* Acquittal Line Activities */}
                <InfoSection title="Acquittal Line Activities" icon={ReceiptLongIcon}>
                    <Box sx={{ mt: 2 }}>
                        <AcquittalLineActivitiesFinanceTable
                            lineActivities={lineActivities || []}
                            formatCurrency={formatCurrency}
                        />
                    </Box>
                </InfoSection>

                {/* Notes Section */}
                <InfoSection title="Additional Notes" icon={ArticleIcon}>
                    <Typography variant="body1">{acquittalData.notes || 'No additional notes provided.'}</Typography>
                </InfoSection>

                {/* Documents Section */}
                <InfoSection title="Acquittal Documents" icon={ArticleIcon}>
                    <DocumentSection documents={acquittalDocuments} theme={theme} keycloak={keycloak} />
                </InfoSection>
            </DialogContent>

            <DialogActions
                sx={{
                    p: 3,
                    gap: 2,
                    bgcolor: theme.background.light,
                    borderTop: `1px solid ${theme.primary.light}`
                }}
            >
                <ActionButton
                    variant="outlined"
                    onClick={onClose}
                    startIcon={<CloseIcon />}
                    sx={{
                        color: theme.text.secondary,
                        borderColor: theme.text.secondary,
                        '&:hover': {
                            borderColor: theme.text.primary,
                            color: theme.text.primary,
                            bgcolor: 'transparent'
                        }
                    }}
                >
                    Close
                </ActionButton>
            </DialogActions>
        </PreviewDialog>
    );
};

FinanceAcquittalPreviewDialog.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    data: PropTypes.shape({
        id: PropTypes.number,
        createdAt: PropTypes.string,
        status: PropTypes.string,
        totalSpentAmount: PropTypes.number,
        clientReceivedAmount: PropTypes.number,
        varianceAmount: PropTypes.number,
        breakfastVariance: PropTypes.number,
        lunchVariance: PropTypes.number,
        dinnerVariance: PropTypes.number,
        incidentalVariance: PropTypes.number,
        accommodationVariance: PropTypes.number,
        provedAccommodationVariance: PropTypes.number,
        unprovedAccommodationVariance: PropTypes.number,
        branchCode: PropTypes.object,
        department: PropTypes.object,
        user: PropTypes.object
    })
};

export default FinanceAcquittalPreviewDialog; 